import { Panel, SkeletonLoader } from '@plerionhq/design-system';
import { useSearchParams } from 'react-router-dom';
import { useGrantsStats } from '../../api/use-grants-stats';
import {
  applyPresetParams,
  CROSS_ACCOUNT_PARAMS,
  EXTERNAL_PARAMS,
  UNTRUSTED_EXTERNAL_PARAMS,
  type PresetParams,
} from './preset-views';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const EMPTY = '—';

function formatCount(n: number | undefined | null): string {
  if (n === undefined || n === null) {
    return EMPTY;
  }
  return n.toLocaleString();
}

function formatTotal(
  t: TFunction<'accessGrants'>,
  filtered: number,
  absolute: number | undefined | null,
): string {
  if (absolute === undefined || absolute === null) {
    return EMPTY;
  }
  if (filtered === absolute) {
    return formatCount(absolute);
  }
  return t('filteredOfTotal', {
    filtered: formatCount(filtered),
    total: formatCount(absolute),
  });
}

function KpiTile({
  label,
  value,
  status,
  onSelect,
}: {
  label: string;
  value: string;
  status: 'loading' | 'success';
  onSelect?: () => void;
}) {
  const { t } = useTranslation('accessGrants');
  const body = (
    <SkeletonLoader status={status} type="line">
      <div className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
        {label}
      </div>
      <div className="mt-1 text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
        {value}
      </div>
    </SkeletonLoader>
  );

  if (!onSelect) {
    return (
      <Panel className="rounded-card! border-container-border border p-4 shadow-none!">
        {body}
      </Panel>
    );
  }

  return (
    <Panel className="rounded-card! border-container-border border p-0 shadow-none!">
      <button
        type="button"
        onClick={onSelect}
        aria-label={t('filterGrantsTo', { label: label.toLowerCase() })}
        className="w-full cursor-pointer rounded-md p-4 text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:hover:bg-gray-700"
      >
        {body}
      </button>
    </Panel>
  );
}

type GrantsKpisProperties = {
  filteredTotal: number;
};

export function GrantsKpis({ filteredTotal }: GrantsKpisProperties) {
  const { t } = useTranslation('accessGrants');
  const { data: stats, isLoading } = useGrantsStats();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = isLoading ? 'loading' : 'success';

  const totalValue = formatTotal(t, filteredTotal, stats?.total);

  const select = (params: PresetParams) => () =>
    setSearchParams(applyPresetParams(searchParams, params));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiTile label={t('totalGrants')} value={totalValue} status={status} />
      <KpiTile
        label={t('externalAccess')}
        value={formatCount(stats?.external)}
        status={status}
        onSelect={select(EXTERNAL_PARAMS)}
      />
      <KpiTile
        label={t('untrustedExternalAccess')}
        value={formatCount(stats?.untrustedExternal)}
        status={status}
        onSelect={select(UNTRUSTED_EXTERNAL_PARAMS)}
      />
      <KpiTile
        label={t('common:crossAccountAccess')}
        value={formatCount(stats?.crossAccount)}
        status={status}
        onSelect={select(CROSS_ACCOUNT_PARAMS)}
      />
    </div>
  );
}
